import type { DataSource, EntityCollection } from "@firecms/core";
import type { Space } from "../../collections/spaces";
import { upsertContentItems } from "../upsertContentItems";
import type { ContentImportCollection, ContentImportResult } from "../types";
import type {
  FlattenedLearningPath,
  LearningPathContentCollections,
  LearningPathImportSuccess,
  LearningPathSpaceDto,
} from "./types";

const emptyResult = (): ContentImportResult => ({ imported: 0, failed: [] });

async function upsertCollection(params: {
  dataSource: DataSource;
  collection: EntityCollection;
  importCollection: ContentImportCollection;
  contentScopeId: { accountId: string; spaceId: string };
  items: FlattenedLearningPath["units"];
}): Promise<ContentImportResult> {
  if (params.items.length === 0) {
    return emptyResult();
  }
  return upsertContentItems({
    dataSource: params.dataSource,
    collection: params.collection,
    importCollection: params.importCollection,
    path: params.collection.path,
    contentScopeId: params.contentScopeId,
    items: params.items,
  });
}

async function saveSpace(params: {
  dataSource: DataSource;
  spaceCollection: EntityCollection<Space>;
  space: LearningPathSpaceDto;
  spaceId?: string;
}): Promise<{ spaceId: string; created: boolean }> {
  const { dataSource, spaceCollection, space, spaceId } = params;

  if (!spaceId) {
    const saved = await dataSource.saveEntity({
      path: spaceCollection.path,
      collection: spaceCollection,
      values: {
        name: space.name,
        title: space.title,
        body: space.body,
      },
      status: "new",
    });
    const createdId = saved?.id;
    if (!createdId) {
      throw new Error("Save succeeded but no learning path id was returned");
    }
    return { spaceId: createdId, created: true };
  }

  const existing = await dataSource.fetchEntity({
    path: spaceCollection.path,
    collection: spaceCollection,
    entityId: spaceId,
  });
  if (!existing) {
    throw new Error(`Learning path ${spaceId} was not found`);
  }

  await dataSource.saveEntity({
    path: spaceCollection.path,
    collection: spaceCollection,
    entityId: spaceId,
    status: "existing",
    values: {
      ...existing.values,
      name: space.name,
      title: space.title,
      body: space.body,
    },
  });

  return { spaceId, created: false };
}

export async function importLearningPath(params: {
  dataSource: DataSource;
  accountId: string;
  spaceCollection: EntityCollection<Space>;
  collectionsFor: (spaceId: string) => LearningPathContentCollections;
  flattened: FlattenedLearningPath;
  spaceId?: string;
}): Promise<LearningPathImportSuccess> {
  const {
    dataSource,
    accountId,
    spaceCollection,
    collectionsFor,
    flattened,
    spaceId: existingSpaceId,
  } = params;

  const { spaceId, created } = await saveSpace({
    dataSource,
    spaceCollection,
    space: flattened.space,
    spaceId: existingSpaceId,
  });

  const contentScopeId = { accountId, spaceId };
  const collections = collectionsFor(spaceId);

  const units = await upsertCollection({
    dataSource,
    collection: collections.units,
    importCollection: "units",
    contentScopeId,
    items: flattened.units,
  });
  const stories = await upsertCollection({
    dataSource,
    collection: collections.stories,
    importCollection: "stories",
    contentScopeId,
    items: flattened.stories,
  });
  const questions = await upsertCollection({
    dataSource,
    collection: collections.questions,
    importCollection: "questions",
    contentScopeId,
    items: flattened.questions,
  });
  const levels = await upsertCollection({
    dataSource,
    collection: collections.levels,
    importCollection: "levels",
    contentScopeId,
    items: flattened.levels,
  });

  return { spaceId, created, units, stories, questions, levels };
}

export function learningPathImportTotals(result: LearningPathImportSuccess): {
  imported: number;
  failed: number;
} {
  const parts = [result.units, result.stories, result.questions, result.levels];
  return {
    imported: parts.reduce((sum, part) => sum + part.imported, 0),
    failed: parts.reduce((sum, part) => sum + part.failed.length, 0),
  };
}

export function learningPathImportFailures(result: LearningPathImportSuccess) {
  return [
    ...result.units.failed.map((row) => ({ ...row, collection: "units" })),
    ...result.stories.failed.map((row) => ({ ...row, collection: "stories" })),
    ...result.questions.failed.map((row) => ({
      ...row,
      collection: "questions",
    })),
    ...result.levels.failed.map((row) => ({ ...row, collection: "levels" })),
  ];
}
